import { ArrowUp, Paperclip, Image as ImageIcon, GitBranch, RotateCcw, MousePointer2, X, Square } from 'lucide-react';
import type { ElementInfo } from '@/lib/visualEdit';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PixelMessageRenderer } from './PixelMessageRenderer';
import { WebContainerPreview } from './WebContainerPreview';
import { VisualEditPanel } from './VisualEditPanel';
import { EditableProjectName } from './EditableProjectName';
import { AIMessage, AIPromptInputModeToggle, AIPromptInputElementBadge } from '@/components/ai-elements';
import { PlanCard, parsePlan, type PlanData } from './PlanCard';
import { ErrorBanner, parseError, type ErrorInfo } from './ErrorBanner';
import { SupabasePanel } from '@/components/integrations/SupabasePanel';
import { SupabaseStatusPopover } from './SupabaseStatusPopover';
import { useSupabaseMCP } from '@/hooks/useSupabaseMCP';
import { type Message } from "ai/react";
import { PxMark } from "@/components/PxMark";
import { supabase } from "@/integrations/supabase/client";
import { type Version } from "@/hooks/useVersionHistory";

interface ProjectViewProps {
  messages: Message[];
  input: string;
  setInput?: (value: string) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  stop: () => void;
  projectId?: string;
  projectName?: string;
  onRenameProject?: (newName: string) => Promise<{ success: boolean; error?: string }>;
  sandpackFiles: Record<string, string>;
  sandpackDependencies?: Record<string, string>;
  sandpackShellActions?: string[];
  sandpackDeletedFiles?: string[];
  previewIsLoading: boolean;
  onNavigateToHome?: () => void;
  append?: (message: { role: 'user'; content: string }) => Promise<any>;
  // Versioning props
  versions?: Version[];
  currentVersionIndex?: number;
  onRollback?: (versionId: string) => Promise<void>;
  isVersionsLoading?: boolean;
}

export function ProjectView({
  messages,
  input = '',  // DEFAULT: Prevent .trim() crash when undefined
  setInput,
  handleInputChange,
  handleSubmit,
  isLoading,
  stop,
  projectId,
  projectName = "Projet",
  onRenameProject,
  sandpackFiles,
  sandpackDependencies = {},
  sandpackShellActions = [],
  sandpackDeletedFiles = [],
  previewIsLoading,
  onNavigateToHome,
  append,
  versions = [],
  currentVersionIndex = -1,
  onRollback,
  isVersionsLoading = false,
}: ProjectViewProps) {
  const [sidebarView, setSidebarView] = useState<'chat' | 'versions'>('chat');
  const [activeTab, setActiveTab] = useState<'apercu' | 'code'>('apercu');
  const [currentView, setCurrentView] = useState(0);
  const [sidebarWidth, setSidebarWidth] = useState(380);
  const [isResizing, setIsResizing] = useState(false);
  const [pendingError, setPendingError] = useState<ErrorInfo | null>(null);
  const [isFixingError, setIsFixingError] = useState(false);
  const [showSupabasePanel, setShowSupabasePanel] = useState(false);
  const [chatMode, setChatMode] = useState<'chat' | 'plan'>('chat');
  const [pendingPlan, setPendingPlan] = useState<PlanData | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedElement, setSelectedElement] = useState<ElementInfo | null>(null);
  const { isConnected: isSupabaseConnected } = useSupabaseMCP(projectId);
  const [searchParams, setSearchParams] = useSearchParams();


  const scrollRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedImage, setSelectedImage] = useState<{ file: File; preview: string; base64: string } | null>(null);

  // Handle image selection
  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image trop lourde (max 5 Mo)');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setSelectedImage({
        file,
        preview: URL.createObjectURL(file),
        base64: base64.split(',')[1]
      });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  // Remove selected image
  const removeImage = useCallback(() => {
    if (selectedImage?.preview) {
      URL.revokeObjectURL(selectedImage.preview);
    }
    setSelectedImage(null);
  }, [selectedImage]);

  const [isUploading, setIsUploading] = useState(false);

  // Upload image to Supabase Storage
  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      setIsUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('chat-images')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        if (uploadError.message.includes('Bucket not found')) {
          alert("Le bucket 'chat-images' n'existe pas sur Supabase. Veuillez le créer.");
        }
        return null;
      }

      const { data } = supabase.storage
        .from('chat-images')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  // Handle build errors - display banner instead of auto-fixing
  const handleBuildError = useCallback((rawError: string) => {
    // Don't show new errors if already fixing
    if (isFixingError) {
      console.log('[ErrorBanner] Skipping - already fixing');
      return;
    }

    console.log('[ErrorBanner] Error detected, showing banner');
    const errorInfo = parseError(rawError);
    setPendingError(errorInfo);
  }, [isFixingError]);

  // Handle user clicking "Try to fix" button
  const handleTryFix = useCallback(() => {
    if (!append || !pendingError) return;

    setIsFixingError(true);

    // Build context with relevant file contents
    const filesToAnalyze = pendingError.files.slice(0, 3); // Limit to 3 files
    let fileContext = '';

    for (const filePath of filesToAnalyze) {
      // Try to find the file in sandpackFiles
      const normalizedPath = filePath.startsWith('/') ? filePath : '/' + filePath;
      const content = sandpackFiles[normalizedPath] || sandpackFiles[filePath] || sandpackFiles[filePath.replace(/^\//, '')];

      if (content) {
        fileContext += `\n### Contenu de \`${filePath}\`:\n\`\`\`tsx\n${content.slice(0, 1500)}${content.length > 1500 ? '\n// ... (tronqué)' : ''}\n\`\`\`\n`;
      }
    }

    // Build concise fix prompt
    const fixPrompt = `🔧 **CORRECTION D'ERREUR**

**Erreur détectée :** ${pendingError.shortMessage}

**Fichiers concernés :** ${pendingError.files.join(', ') || 'Non identifiés'}
${fileContext}
**Erreur complète :**
\`\`\`
${pendingError.rawError?.slice(0, 500) || pendingError.message}
\`\`\`

**Instructions :**
1. 🔍 Analyse le problème
2. ❌ Identifie la cause exacte
3. ✅ Génère UNIQUEMENT le(s) fichier(s) corrigé(s)

Sois CONCIS dans ta réponse.`;

    append({
      role: 'user',
      content: fixPrompt
    }).finally(() => {
      setIsFixingError(false);
      setPendingError(null);
    });
  }, [append, pendingError, sandpackFiles]);

  const handleElementSelected = useCallback((element: ElementInfo) => {
    setSelectedElement(element);
    setSelectionMode(false);
    console.log('[ProjectView] Element selected:', element);
  }, []);

  const handleDismissError = useCallback(() => {
    setPendingError(null);
  }, []);

  // Reset pending error when AI successfully completes (no longer loading)
  useEffect(() => {
    if (!isLoading && !isFixingError) {
      // Clear error if AI just finished generating (likely fixed it)
      // Keep error if user dismissed or hasn't tried to fix yet
    }
  }, [isLoading, isFixingError]);


  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Plan confirmation handler
  const handlePlanConfirm = useCallback((plan: PlanData) => {
    if (!append) return;
    setPendingPlan({ ...plan, status: 'confirmed' });
    // Send execute command to AI
    append({
      role: 'user',
      content: `[EXECUTE_PLAN: ${plan.id}] Exécute le plan "${plan.title}" que tu viens de proposer.`
    });
  }, [append]);

  // Plan rejection handler
  const handlePlanReject = useCallback((plan: PlanData) => {
    setPendingPlan({ ...plan, status: 'rejected' });
    // User can now type modifications
  }, []);

  // Custom form submit handler to add [PLAN_MODE] prefix and handle images
  const handleFormSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if ((!input.trim() && !selectedImage) || !append || isUploading) return;

    let finalMessage = input.trim();
    let imageUrl = null;

    if (selectedImage) {
      // Upload to Supabase
      imageUrl = await uploadImage(selectedImage.file);

      if (!imageUrl) {
        alert("Échec de l'upload de l'image. Vérifiez votre connexion Supabase.");
        return;
      }
    }

    let messageContent = chatMode === 'plan'
      ? `[PLAN_MODE] ${finalMessage}`
      : finalMessage;

    // If element is selected (Visual Edit), add context for AI
    if (selectedElement) {
      const elementInfo = `[ÉLÉMENT SÉLECTIONNÉ: <${selectedElement.tagName.toLowerCase()}${selectedElement.className ? ` class="${selectedElement.className.split(' ')[0]}"` : ''}>${selectedElement.textContent ? ` "${selectedElement.textContent.slice(0, 50)}..."` : ''}]`;
      messageContent = `${elementInfo}\n${messageContent}`;
      // Clear selection after sending
      setSelectedElement(null);
    }

    // If image is selected, add special marker for the API
    if (imageUrl) {
      messageContent = `[IMAGE_ASSET:${imageUrl}] ${messageContent || 'Analyse cette maquette et reproduis-la'}`;
    }

    // Reset pending plan when new message is sent
    if (pendingPlan) {
      setPendingPlan(null);
    }

    // Use append which sends and triggers generation
    append({ role: 'user', content: messageContent });
    setInput?.(''); // Clear input
    removeImage();
  }, [chatMode, input, append, pendingPlan, selectedImage, removeImage, setInput, selectedElement, isUploading]);

  // Format relative time from ISO string
  const formatRelativeTime = (isoDate: string) => {
    const date = new Date(isoDate);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `il y a ${diffMins} min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `il y a ${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    return `il y a ${diffDays}j`;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing) return;
    const newWidth = e.clientX;
    if (newWidth >= 280 && newWidth <= 600) {
      setSidebarWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      formRef.current?.requestSubmit();
    }
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Left Sidebar - Conversation */}
      <div
        className="flex flex-col bg-card border-r border-border relative z-20"
        style={{ width: `${sidebarWidth}px` }}
      >
        {/* Sidebar Header */}
        <div className="px-4 flex items-center justify-between border-b border-border shrink-0 bg-card">
          <div className="flex items-center gap-2 py-3">
            <button
              onClick={onNavigateToHome}
              title="Retour à l'accueil"
              className="p-1.5 hover:bg-secondary transition-colors flex items-center justify-center"
            >
              <PxMark size={16} label="Pixel" />
            </button>
            {onRenameProject ? (
              <EditableProjectName name={projectName} onRename={onRenameProject} />
            ) : (
              <span className="text-sm font-medium text-foreground">{projectName}</span>
            )}
          </div>

          {/* View Toggle */}
          <div className="flex items-stretch self-stretch">
            <button
              onClick={() => setSidebarView('chat')}
              className={`px-3 text-[13px] font-medium border-b-2 -mb-px transition-colors ${sidebarView === 'chat' ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              Chat
            </button>
            <button
              onClick={() => setSidebarView('versions')}
              className={`px-3 text-[13px] font-medium border-b-2 -mb-px transition-colors ${sidebarView === 'versions' ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              Versions
            </button>
          </div>
        </div>

        {sidebarView === 'chat' ? (
          <>
            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
              {messages.map((message) => (
                <AIMessage
                  key={message.id}
                  from={message.role as 'user' | 'assistant'}
                  variant="flat"
                  avatar={{
                    icon: message.role === 'assistant'
                      ? <PxMark size={16} label="Pixel" />
                      : undefined,
                    name: message.role === 'user' ? 'Vous' : 'Pixel'
                  }}
                >
                  {message.role === 'assistant' ? (
                    <PixelMessageRenderer
                      message={message}
                      projectId={projectId}
                      onPlanConfirm={handlePlanConfirm}
                      onPlanReject={handlePlanReject}
                      onActionResult={(result) => {
                        if (append) {
                          // We send this as a USER message but with a system tag so the AI processes it as tool output
                          append({
                            role: 'user',
                            content: `[SUPABASE_ACTION_RESULT]\n${result}`
                          });
                        }
                      }}
                      planStatus={pendingPlan?.id === message.id ? pendingPlan.status : 'pending'}
                    />
                  ) : (
                    <PixelMessageRenderer message={message} />
                  )}
                </AIMessage>
              ))}

              {isLoading && (
                <div className="flex items-center gap-2 pl-8">
                  <PxMark state="generating" size={16} />
                  <span className="font-mono text-[11px] text-faint">Génération…</span>
                </div>
              )}

              {/* Error Banner removed - now displayed as overlay on preview */}
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-border shrink-0 bg-card z-30">
              <form ref={formRef} onSubmit={handleFormSubmit}>
                <div
                  className="overflow-hidden bg-card border border-hairline-strong relative group transition-colors focus-within:border-foreground focus-within:ring-2 focus-within:ring-ring"
                >
                  {/* Selected Element Context Badge */}
                  {selectedElement && (
                    <AIPromptInputElementBadge
                      elementTag={selectedElement.tagName.toLowerCase()}
                      elementClass={selectedElement.className ? selectedElement.className.split(' ')[0] : undefined}
                      onRemove={() => setSelectedElement(null)}
                    />
                  )}
                  <textarea
                    value={input}
                    onChange={handleInputChange}
                    onKeyDown={onKeyDown}
                    placeholder="Demander à l'IA…"
                    className="w-full px-4 py-3 bg-transparent border-none outline-none resize-none placeholder:text-faint min-h-[50px] max-h-[200px] text-sm text-foreground"
                    rows={1}
                    style={{ fieldSizing: "content" } as any}
                  />

                  <div className="flex items-center justify-between px-2 py-2 border-t border-border">
                    <div className="flex items-center gap-1">
                      {/* Chat/Plan Mode Toggle */}
                      <AIPromptInputModeToggle
                        mode={chatMode}
                        onChange={setChatMode}
                        className="mr-1"
                      />
                      <button
                        type="button"
                        onClick={() => setSelectionMode(!selectionMode)}
                        className={`p-1.5 transition-colors ${selectionMode ? 'bg-brand-wash text-brand-ink' : 'text-muted-foreground hover:text-foreground hover:bg-secondary'}`}
                        title={selectionMode ? 'Désactiver la sélection' : 'Cibler un élément'}
                      >
                        <MousePointer2 className="w-4 h-4" />
                      </button>
                      {/* Hidden file input */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageSelect}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="p-1.5 transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary"
                        title="Joindre une maquette"
                      >
                        <Paperclip className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="p-1.5 transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary"
                        title="Joindre une image"
                      >
                        <ImageIcon className="w-4 h-4" />
                      </button>
                      {/* Image preview */}
                      {selectedImage && (
                        <div className="relative ml-2">
                          <img
                            src={selectedImage.preview}
                            alt="Maquette"
                            className="h-8 w-8 object-cover border border-border"
                          />
                          <button
                            type="button"
                            onClick={removeImage}
                            title="Retirer l'image"
                            className="absolute -top-1 -right-1 p-0.5 bg-err text-background"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      )}
                    </div>

                    {isLoading ? (
                      <button
                        type="button"
                        onClick={stop}
                        title="Arrêter la génération"
                        className="p-2 bg-err text-background hover:bg-err/90 transition-colors"
                      >
                        <Square className="w-3 h-3 fill-current" />
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={!input?.trim()}
                        title="Envoyer"
                        className="p-2 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </>
        ) : (
          <>
            {/* Versions View */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {isVersionsLoading ? (
                <div className="flex flex-col items-center justify-center py-8 gap-3">
                  <PxMark state="thinking" size={20} />
                  <span className="font-mono text-[11px] text-faint">Chargement…</span>
                </div>
              ) : versions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <PxMark size={20} />
                  <p className="text-muted-foreground text-sm">Aucune version sauvegardée</p>
                  <p className="font-mono text-[11px] text-faint">Les versions sont créées automatiquement</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {versions.map((version, index) => (
                    <div
                      key={version.id}
                      className="group cursor-pointer"
                    >
                      <div
                        className={`overflow-hidden transition-colors border ${index === currentVersionIndex ? 'border-border border-l-2 border-l-foreground bg-card' : 'border-border bg-card hover:bg-secondary'}`}
                      >
                        {/* Preview Thumbnail: Placeholder */}
                        <div className="aspect-[16/10] bg-surface-3 overflow-hidden relative border-b border-border">
                          <div className="absolute inset-0 flex items-center justify-center font-mono text-[11px] text-faint">
                            Aperçu v{version.versionNumber}
                          </div>
                        </div>

                        {/* Version Info */}
                        <div className="p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <GitBranch className={`w-3.5 h-3.5 ${index === currentVersionIndex ? 'text-foreground' : 'text-faint'}`} />
                              <span className="text-sm font-medium text-foreground">
                                Version {version.versionNumber}
                              </span>
                              {index === currentVersionIndex && (
                                <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-brand-wash text-brand-ink">
                                  Active
                                </span>
                              )}
                            </div>
                            <span className="font-mono text-[10px] text-faint">
                              {formatRelativeTime(version.createdAt)}
                            </span>
                          </div>
                          <p className="text-xs leading-relaxed text-muted-foreground">
                            {version.description}
                          </p>

                          {index !== currentVersionIndex && onRollback && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onRollback(version.id);
                              }}
                              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors mt-2 bg-card border border-border text-foreground hover:bg-secondary"
                            >
                              <RotateCcw className="w-3 h-3" />
                              Restaurer
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Resize Handle */}
        <div
          onMouseDown={handleMouseDown}
          className="absolute top-0 right-0 h-full w-1 cursor-col-resize group z-50 hover:w-1.5 transition-all"
          style={{ right: '-2px' }}
        >
          <div
            className="absolute top-0 right-0 h-full w-[2px] transition-opacity opacity-0 group-hover:opacity-100 bg-brand"
          />
        </div>
      </div>

      {/* Main Content Area - WebContainer Preview */}
      <div className="flex-1 flex flex-col overflow-hidden bg-background">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-background shrink-0">
          {/* Supabase status badge */}
          <div className="flex items-center gap-2">
            <SupabaseStatusPopover onOpenPanel={() => setShowSupabasePanel(true)} pixelProjectId={projectId} />
          </div>
        </div>

        {/* Project Preview (WebContainer) - hidden when Supabase panel is open */}
        <div className={`flex-1 overflow-hidden flex flex-row relative ${isResizing ? 'pointer-events-none' : ''}`}>
          {/* Supabase Panel - replaces preview when open */}
          {showSupabasePanel && isSupabaseConnected ? (
            <div className="flex-1 flex flex-col">
              <SupabasePanel onClose={() => setShowSupabasePanel(false)} pixelProjectId={projectId} />
            </div>
          ) : (
            <div className="flex-1 flex flex-col">
              <WebContainerPreview
                key={projectId || 'default'}
                files={sandpackFiles}
                dependencies={sandpackDependencies}
                shellActions={sandpackShellActions}
                deletedFiles={sandpackDeletedFiles}
                isLoading={previewIsLoading}
                isGenerating={isLoading && Object.keys(sandpackFiles).length > 0}
                onBuildError={append ? handleBuildError : undefined}
                projectId={projectId}
                projectName={projectName}
                selectionMode={selectionMode}
                onElementSelected={handleElementSelected}
                pendingError={pendingError}
                onTryFix={handleTryFix}
                onDismissError={handleDismissError}
                isFixingError={isFixingError}
              />
            </div>
          )}

          {/* Visual Edit Panel */}
          {selectedElement && (
            <div className="w-80 shrink-0">
              <VisualEditPanel
                element={selectedElement}
                onSelectElement={(element) => {
                  // Drill-down selection: send message to iframe to force select child
                  const iframe = document.querySelector('iframe') as HTMLIFrameElement;
                  if (iframe?.contentWindow && element.xpath) {
                    iframe.contentWindow.postMessage({
                      type: 'PIXEL_SELECT_BY_XPATH',
                      xpath: element.xpath
                    }, '*');
                  }
                }}
                onApplyLive={(styles) => {
                  // Send live styles to iframe
                  const iframe = document.querySelector('iframe') as HTMLIFrameElement;
                  if (iframe?.contentWindow && selectedElement.xpath) {
                    iframe.contentWindow.postMessage({
                      type: 'PIXEL_APPLY_STYLES',
                      xpath: selectedElement.xpath,
                      styles
                    }, '*');
                  }
                }}
                onSave={(changes) => {
                  // Create prompt for AI with selected element context
                  const modificationPrompt = `[MODIFICATION VISUELLE]
Modifie l'élément suivant dans le code source:
- Élément: ${selectedElement.tagName.toLowerCase()}${selectedElement.className ? '.' + selectedElement.className.split(' ')[0] : ''}
- XPath: ${selectedElement.xpath}
${changes.content ? `- Nouveau contenu: "${changes.content}"` : ''}
${changes.imageSrc ? `- Nouvelle image: "${changes.imageSrc}"` : ''}
${Object.keys(changes.styles || {}).length > 0 ? `- Nouveaux styles: ${JSON.stringify(changes.styles)}` : ''}

Applique ces modifications en utilisant des classes Tailwind quand c'est possible.`;

                  // Auto-submit to AI
                  if (append) {
                    append({ role: 'user', content: modificationPrompt });
                  }
                  setSelectedElement(null);
                }}
                onCancel={() => {
                  // Reset styles in iframe
                  const iframe = document.querySelector('iframe') as HTMLIFrameElement;
                  if (iframe?.contentWindow && selectedElement.xpath) {
                    iframe.contentWindow.postMessage({
                      type: 'PIXEL_RESET_STYLES',
                      xpath: selectedElement.xpath
                    }, '*');
                  }
                  setSelectedElement(null);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
